import { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import HtmlWithLatex from './HtmlWithLatex';
import { MathInputTools } from '../../Student/components/TypeDrawQuestionInput';
import { fillBlankUnderlineStyle } from './fillBlankStyles';

const BLANK_PLACEHOLDERS = ['□', '[ ]', '[]', '__', '___', '_____', '\\Box', '\\square', '\\Square'];
const stripMathDelimiters = (value) => String(value || '').replace(/\$/g, '').trim();

/**
 * Renders an interactive hub-and-spoke fill-in-the-blank diagram.
 *
 * Layout (4-spoke, like Q25 "fill in so answer is always 6"):
 *
 *   [TL box] ──────────────── [TR box]
 *       \                        /
 *        \──→  ( hub value ) ←──/
 *        /                       \
 *   [BL box] ──────────────── [BR box]
 *
 * Modes:
 *   - interactive (inputValues + onInputChange provided): editable inputs inside each box
 *   - review     (showAnswers=true): correct answer shown; student answer coloured green/red
 *   - paper      (neither):         blank dashed underline inside each box
 *
 * Props:
 *   blankLabels   string[]          – expressions per box, e.g. ["½ + □ = 6", "3.5 + □ = 6"]
 *   hubValue      string | undefined – centre oval text; auto-detected from labels if omitted
 *   inputValues   string[] | undefined
 *   onInputChange (idx: number, value: string) => void
 *   showAnswers   boolean
 *   correctBlanks any[]             – correct answer per blank (same shape as answer_data.correct_blanks)
 *   studentAnswers any              – student submitted answers (array or scalar)
 */
const DiagramHubSpoke = ({
  blankLabels = [],
  hubValue: hubValueProp,
  inputValues,
  onInputChange,
  showAnswers = false,
  correctBlanks = [],
  studentAnswers,
  showMathTools = false,
}) => {
  const markerIdRef = useRef(`hs_${Math.random().toString(36).slice(2, 7)}`);
  const markerId = markerIdRef.current;
  const blankInputRefs = useRef({});
  const [activeBlankIdx, setActiveBlankIdx] = useState(0);

  if (!blankLabels || blankLabels.length === 0) return null;

  const items = blankLabels.slice(0, 4);
  const n = items.length;

  // ── Expression parsing ────────────────────────────────────────────────────
  // Returns the static part of the label for the blue expression cell.
  //
  // Strategy:
  //   1. Strip any "= N" hub-target suffix first (e.g. "½ + □ = 6" → "½ + □")
  //   2. Find the blank placeholder position:
  //      • Blank at END or MIDDLE → show text BEFORE blank  ("1/16 + □" → "1/16 +")
  //      • Blank at START         → show text AFTER  blank  ("□ - 2/3"  → "- 2/3")
  //   This prevents empty cells when the variable slot is the leftmost token.
  const parseExpression = (label) => {
    const s = stripMathDelimiters(label);
    // Strip hub-target suffix first
    const withoutTarget = s.replace(/\s*=\s*\S+\s*$/, '').trim();
    for (const blank of BLANK_PLACEHOLDERS) {
      const idx = withoutTarget.indexOf(blank);
      if (idx < 0) continue;
      const before = withoutTarget.substring(0, idx).trim();
      const after  = withoutTarget.substring(idx + blank.length).trim();
      // When blank is at the start, show what follows it
      return before !== '' ? before : after;
    }
    return withoutTarget;
  };

  // Auto-detect hub value from "= N" pattern at end of first label.
  // Strip any accidental leading "=" so the oval shows "3/8" not "=3/8".
  const hubValue = (() => {
    const raw = hubValueProp || (() => {
      for (const label of items) {
        const m = stripMathDelimiters(label).match(/=\s*(\S+)\s*$/);
        if (m) return m[1];
      }
      return '';
    })();
    return String(raw).replace(/^=\s*/, '').trim();
  })();

  // ── Student answers normalisation ────────────────────────────────────────
  const studentArr = (() => {
    if (Array.isArray(studentAnswers)) return studentAnswers;
    if (studentAnswers != null && studentAnswers !== '') return [studentAnswers];
    return [];
  })();

  const getCorrectAnswer = (idx) => {
    const b = correctBlanks[idx];
    if (b == null) return '';
    if (Array.isArray(b)) return String(b[0] ?? '');
    if (typeof b === 'object' && b.text != null) return String(b.text);
    return String(b);
  };

  // Normalize LaTeX fractions for comparison: "\frac{3}{4}" → "3/4"
  const normForMatch = (v) => {
    let s = String(v).replace(/^\$+|\$+$/g, '').trim().toLowerCase();
    s = s.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1/$2');
    return s;
  };

  const isStudentCorrect = (idx) => {
    const s = normForMatch(studentArr[idx] ?? '');
    const c = normForMatch(getCorrectAnswer(idx));
    return s !== '' && c !== '' && s === c;
  };

  // ── Box corner positions as % of container ────────────────────────────────
  // Each box: width=32%, height=25%
  // Positions are [left%, top%] for the box's top-left corner
  const positionSets = {
    4: [[0, 0], [68, 0], [0, 75], [68, 75]],           // 4 corners
    3: [[0, 0], [68, 0], [34, 75]],                     // TL, TR, bottom-centre
    2: [[0, 37.5], [68, 37.5]],                         // left / right (centred vertically)
    1: [[34, 0]],                                       // top-centre
  };
  const positions = (positionSets[n] || positionSets[4]).slice(0, n);

  // Centre of each box in % (box centre = position + half-size)
  const boxCenters = positions.map(([l, t]) => [l + 16, t + 12.5]);

  // ── Hub oval centre and radii (all in viewBox % units) ───────────────────
  const HCX = 50, HCY = 50, HRX = 13, HRY = 9;

  // ── Arrow geometry ────────────────────────────────────────────────────────
  // Each arrow goes from near the box edge to the oval surface.
  const computeArrow = (bx, by) => {
    const dx = HCX - bx;
    const dy = HCY - by;
    const mag = Math.sqrt(dx * dx + dy * dy);
    if (mag < 0.01) return null;
    const ux = dx / mag, uy = dy / mag;

    // Arrow start: 9 units from box centre toward hub (clears the box edge)
    const x1 = bx + ux * 9;
    const y1 = by + uy * 9;

    // Arrow end: oval surface.  t = 1 / sqrt((ux/rx)^2 + (uy/ry)^2)
    const t = 1 / Math.sqrt((ux / HRX) ** 2 + (uy / HRY) ** 2);
    const x2 = HCX - ux * t;
    const y2 = HCY - uy * t;

    return { x1, y1, x2, y2 };
  };

  const arrows = boxCenters.map(([bx, by]) => computeArrow(bx, by));
  const interactive = inputValues != null && typeof onInputChange === 'function';
  const showFillMathTools = showMathTools && interactive;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 480,
        mx: 'auto',
        my: 1,
        p: 1.5,
        bgcolor: 'white',
      }}
    >
      {showFillMathTools ? (
        <MathInputTools
          getInput={() => blankInputRefs.current[activeBlankIdx] ?? null}
          getValue={() => inputValues[activeBlankIdx] ?? ''}
          onChange={(newVal) => onInputChange(activeBlankIdx, newVal)}
        />
      ) : null}
      {/* Aspect-ratio container: 500 × 320 ≈ 64% */}
      <Box sx={{ paddingBottom: '64%', position: 'relative' }}>

        {/* ── SVG layer: hub oval + spoke arrows ─────────────────────────── */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            pointerEvents: 'none',
            overflow: 'visible',
          }}
        >
          <defs>
            <marker
              id={markerId}
              markerWidth="5"
              markerHeight="5"
              refX="4.5"
              refY="2.5"
              orient="auto"
            >
              <path d="M0,0 L0,5 L5,2.5 z" fill="#4a90d9" />
            </marker>
          </defs>

          {/* Spoke arrows */}
          {arrows.map((a, i) =>
            a ? (
              <line
                key={i}
                x1={a.x1} y1={a.y1}
                x2={a.x2} y2={a.y2}
                stroke="#4a90d9"
                strokeWidth="0.7"
                markerEnd={`url(#${markerId})`}
              />
            ) : null
          )}

          {/* Centre hub oval */}
          <ellipse
            cx={HCX} cy={HCY}
            rx={HRX} ry={HRY}
            fill="#cce5ff"
            stroke="#4a90d9"
            strokeWidth="0.9"
          />

          {/* Hub value label */}
          {hubValue && (
            <text
              x={HCX}
              y={HCY}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="6.5"
              fontWeight="bold"
              fontFamily="Arial, Helvetica, sans-serif"
              fill="#1565c0"
            >
              {hubValue}
            </text>
          )}
        </svg>

        {/* ── Spoke boxes (HTML overlay, % positioned) ───────────────────── */}
        {items.map((label, idx) => {
          const [leftPct, topPct] = positions[idx];
          const expr = parseExpression(label);
          const correctAns = getCorrectAnswer(idx);
          const studentAns = studentArr[idx];
          const hasStudent = studentAns != null && String(studentAns).trim() !== '';
          const correct = isStudentCorrect(idx);

          return (
            <Box
              key={idx}
              sx={{
                position: 'absolute',
                left: `${leftPct}%`,
                top: `${topPct}%`,
                width: '32%',
                height: '25%',
                display: 'flex',
                alignItems: 'stretch',
                border: '1.5px solid #4a90d9',
                borderRadius: '4px',
                overflow: 'hidden',
                bgcolor: 'white',
                boxShadow: '0 1px 4px rgba(74,144,217,0.2)',
              }}
            >
              {/* Expression label – blue background section */}
              <Box
                sx={{
                  bgcolor: '#cce5ff',
                  flex: '0 0 58%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 0.75,
                  borderRight: '1px solid #4a90d9',
                  color: '#1565c0',
                  fontWeight: 700,
                  overflow: 'hidden',
                  // Shrink font to fit – cascade to children via inherit
                  fontSize: { xs: '0.55rem', sm: '0.7rem', md: '0.78rem' },
                  '& *': { fontSize: 'inherit !important', lineHeight: '1.2 !important' },
                }}
              >
                <HtmlWithLatex html={expr} />
              </Box>

              {/* Answer slot – white section */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 0.5,
                  minWidth: 0,
                }}
              >
                {interactive ? (
                  /* ── Exam-take mode: editable input ── */
                  <input
                    type="text"
                    value={inputValues[idx] ?? ''}
                    onChange={(e) => onInputChange(idx, e.target.value)}
                    onFocus={() => setActiveBlankIdx(idx)}
                    ref={(el) => { blankInputRefs.current[idx] = el; }}
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      textAlign: 'center',
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      ...fillBlankUnderlineStyle,
                      background: 'transparent',
                      padding: '6px 2px 2px 2px',
                      marginTop: '6px',
                      color: '#1a1a1a',
                    }}
                    placeholder="?"
                    aria-label={`Answer for: ${expr}`}
                  />
                ) : showAnswers ? (
                  /* ── Review mode: student + correct answer ── */
                  <Box sx={{
                    textAlign: 'center',
                    lineHeight: 1.2,
                    px: 0.25,
                    fontSize: '0.6rem',
                    '& *': { fontSize: 'inherit !important', lineHeight: '1.2 !important' },
                  }}>
                    {hasStudent && (
                      <Box
                        sx={{
                          fontWeight: 700,
                          color: correct ? 'success.main' : 'error.main',
                          lineHeight: 1.2,
                        }}
                      >
                        <HtmlWithLatex html={String(studentAns)} />
                      </Box>
                    )}
                    <Box
                      sx={{
                        fontWeight: 700,
                        color: 'success.dark',
                        lineHeight: 1.2,
                        borderTop: hasStudent && !correct ? '1px solid #e0e0e0' : 'none',
                        pt: hasStudent && !correct ? '1px' : 0,
                      }}
                    >
                      ✓ <HtmlWithLatex html={correctAns} />
                    </Box>
                  </Box>
                ) : (
                  /* ── Paper / read-only mode: question mark ── */
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '0.9rem' },
                      color: '#aaa',
                      lineHeight: 1,
                    }}
                  >
                    ?
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default DiagramHubSpoke;
